import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSocket } from '../hooks/useSocket';
import { useSession } from '../hooks/useSession';
import { useTimer } from '../hooks/useTimer';
import { getSession, updateStory } from '../services/apiService';
import { getFacilitatorToken } from '../utils/localStorage';
import { useToast } from '../components/common/Toast';
import { Card } from '../components/common/Card';
import { CelebrationOverlay } from '../components/common/CelebrationOverlay';
import { SessionHeader } from '../components/session/SessionHeader';
import { StoryInput } from '../components/session/StoryInput';
import { CollapsibleStory } from '../components/session/CollapsibleStory';
import { PointsGrid } from '../components/session/PointsGrid';
import { PlayerPanel } from '../components/session/PlayerPanel';
import { EmptyState } from '../components/session/EmptyState';
import type { VoteValue, VoteSummary } from '../types/index';

export function SessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [participantName, setParticipantName] = useState('');
  const [nameSubmitted, setNameSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [votesRevealed, setVotesRevealed] = useState(false);
  const [voteSummary, setVoteSummary] = useState<VoteSummary | null>(null);
  const [revealedVotes, setRevealedVotes] = useState<
    Array<{ name: string; value: VoteValue }>
  >([]);
  const [showCelebration, setShowCelebration] = useState(false);

  const {
    sessionId: currentSessionId,
    state,
    participants,
    story,
    myVote,
    isFacilitator,
    expiresAt,
    voteStartTime,
    setSessionId,
    setState,
    setParticipants,
    setStory,
    setMyVote,
    setIsFacilitator,
    setExpiresAt,
    setVoteStartTime,
    setFacilitatorToken,
  } = useSession();

  const { elapsed } = useTimer(voteStartTime);

  const {
    connected,
    joinSession,
    castVote,
    showVotes,
    resetVotes,
    reopenVoting,
    onSessionState,
    onVotesRevealed,
    onVotesReset,
    onVotingReopened,
    onSessionExpired,
    onError,
    disconnect,
    socketId,
  } = useSocket();

  // Initialize session ID
  useEffect(() => {
    if (sessionId && sessionId !== currentSessionId) {
      setSessionId(sessionId);

      // Check if facilitator token was passed from HomePage
      const facilitatorToken =
        (location.state as any)?.facilitatorToken ||
        getFacilitatorToken(sessionId);
      if (facilitatorToken) {
        setFacilitatorToken(sessionId, facilitatorToken);
      }

      // Load session data
      getSession(sessionId)
        .then((session) => {
          setState(session.state);
          setParticipants(session.participants);
          setStory(session.story);
          setExpiresAt(session.expiresAt);
          setVoteStartTime(session.voteStartTime);
        })
        .catch((err: any) => {
          // Only show error if it's not an expected 404 (session expired/not found)
          // Expected errors are handled by socket error handlers
          if (!err.isExpected) {
            setError(err.message);
          }
        });
    }
  }, [sessionId, currentSessionId, location.state, setSessionId, setState, setParticipants, setStory, setExpiresAt, setVoteStartTime, setFacilitatorToken]);

  // Handle name submission
  const handleNameSubmit = useCallback(() => {
    if (!participantName.trim() || !sessionId) {
      setError('Please enter your name');
      showToast('Please enter your name', 'error');
      return;
    }

    const facilitatorToken = getFacilitatorToken(sessionId);
    joinSession(sessionId, participantName.trim(), facilitatorToken || undefined);
    setNameSubmitted(true);
    setError(null);
    showToast(`Joined session as ${participantName.trim()}`, 'success');
  }, [participantName, sessionId, joinSession, showToast]);

  // Handle vote
  const handleVote = useCallback(
    (value: VoteValue) => {
      if (!sessionId) {
        console.error('[SessionPage.handleVote] No sessionId available');
        return;
      }
      castVote(sessionId, value);
      setMyVote(value);
      showToast(`Voted ${value === '?' ? '?' : value}`, 'success');
    },
    [sessionId, castVote, setMyVote, showToast],
  );

  // Handle show votes
  const handleShowVotes = useCallback(() => {
    if (!sessionId) return;
    showVotes(sessionId);
    showToast('Revealing votes...', 'info');
  }, [sessionId, showVotes, showToast]);

  // Handle reopen voting
  const handleReopenVoting = useCallback(() => {
    if (!sessionId) return;
    reopenVoting(sessionId);
    setVotesRevealed(false);
    setVoteSummary(null);
    setRevealedVotes([]);
    showToast('Reopening voting...', 'info');
  }, [sessionId, reopenVoting, showToast]);

  // Handle reset votes
  const handleResetVotes = useCallback(() => {
    if (!sessionId) return;
    resetVotes(sessionId);
    setMyVote(null);
    setVotesRevealed(false);
    setVoteSummary(null);
    setRevealedVotes([]);
    showToast('Resetting votes...', 'info');
  }, [sessionId, resetVotes, setMyVote, showToast]);

  // Handle copy link
  const handleCopyLink = useCallback(() => {
    if (!sessionId) return;
    const joinUrl = `${window.location.origin}/session/${sessionId}`;
    navigator.clipboard.writeText(joinUrl);
    showToast('Join link copied to clipboard!', 'success');
  }, [sessionId, showToast]);

  // Handle leave session
  const handleLeaveSession = useCallback(() => {
    disconnect();
    showToast('Left session', 'info');
    navigate('/');
  }, [disconnect, navigate, showToast]);

  // Handle story update (debounced by StoryInput component)
  const handleStoryUpdate = useCallback(
    async (newStory: string) => {
      if (!sessionId) return;
      const facilitatorToken = getFacilitatorToken(sessionId);
      if (facilitatorToken) {
        try {
          await updateStory(sessionId, facilitatorToken, newStory);
          // Update local state - StoryInput component will ignore this while user is typing
          setStory(newStory);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to update story';
          setError(errorMessage);
          showToast(errorMessage, 'error');
        }
      }
    },
    [sessionId, setStory, showToast],
  );

  // Set up WebSocket event listeners
  useEffect(() => {
    if (!sessionId || !nameSubmitted) return;

    const unsubscribeState = onSessionState((data) => {
      setState(data.state);
      setParticipants(data.participants);
      setStory(data.story);
      setExpiresAt(data.expiresAt);
      setVoteStartTime(data.voteStartTime);

      // Update my vote if I voted
      const me = data.participants.find((p) => p.id === socketId);
      if (me?.voted && me.value !== undefined) {
        setMyVote(me.value);
      }
    });

    const unsubscribeRevealed = onVotesRevealed((data) => {
      setVotesRevealed(true);
      setVoteSummary(data.summary);
      setRevealedVotes(data.votes);
      setState('revealed');
      showToast('Votes revealed!', 'success');
      
      // Trigger celebration animation on consensus
      if (data.summary?.hasConsensus) {
        setShowCelebration(true);
      }
    });

    const unsubscribeReset = onVotesReset(() => {
      setVotesRevealed(false);
      setVoteSummary(null);
      setRevealedVotes([]);
      setState('collecting');
      setMyVote(null);
      setStory(undefined); // Clear story for next ticket
      setShowCelebration(false); // Reset celebration state
      showToast('Votes reset. Ready for next round!', 'info');
    });

    const unsubscribeReopened = onVotingReopened(() => {
      setVotesRevealed(false);
      setVoteSummary(null);
      setRevealedVotes([]);
      setState('collecting');
      setShowCelebration(false); // Reset celebration state
      showToast('Voting reopened. You can change your vote!', 'info');
    });

    const unsubscribeExpired = onSessionExpired((reason) => {
      const message = 'Session expired';
      setError(message);
      showToast(message, 'error');
      setTimeout(() => {
        navigate('/');
      }, 3000);
    });

    const unsubscribeError = onError((message, code) => {
      // Only log and show errors for unexpected issues
      // Don't spam console/UI for expected errors like session expired when tab is idle
      const isExpectedError = code === 'SESSION_NOT_FOUND' || code === 'SESSION_EXPIRED';
      
      if (!isExpectedError || process.env.NODE_ENV === 'development') {
        console.error('[SessionPage] Socket error:', { message, code, sessionId });
      }
      
      // Only show toast for unexpected errors or first occurrence of expected errors
      if (!isExpectedError) {
        setError(message);
        showToast(message, 'error');
      } else if (code === 'SESSION_EXPIRED') {
        // Handle expired session gracefully - redirect after showing message once
        setError('Session expired');
        showToast('Session expired', 'error');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    });

    return () => {
      unsubscribeState();
      unsubscribeRevealed();
      unsubscribeReset();
      unsubscribeReopened();
      unsubscribeExpired();
      unsubscribeError();
    };
  }, [
    sessionId,
    nameSubmitted,
    onSessionState,
    onVotesRevealed,
    onVotesReset,
    onVotingReopened,
    onSessionExpired,
    onError,
    setState,
    setParticipants,
    setStory,
    setExpiresAt,
    setVoteStartTime,
    setMyVote,
    navigate,
    socketId,
    showToast,
  ]);

  // Track if component is mounted to prevent disconnection during StrictMode
  const isMountedRef = useRef(true);
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Cleanup on unmount - only disconnect when truly unmounting
  useEffect(() => {
    return () => {
      // Use requestIdleCallback or setTimeout to defer cleanup
      // This allows React StrictMode to finish its double-mount cycle
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => {
          if (!isMountedRef.current) {
            disconnect();
          }
        });
      } else {
        setTimeout(() => {
          if (!isMountedRef.current) {
            disconnect();
          }
        }, 0);
      }
    };
  }, [disconnect]);

  // Show name input if not submitted
  if (!nameSubmitted) {
    return (
      <div className="max-w-md mx-auto">
        <Card>
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              Join Session
            </h2>
            <p className="text-gray-600 mb-6">
              Enter your name to join the planning poker session
            </p>
            <div className="space-y-4">
              <input
                type="text"
                value={participantName}
                onChange={(e) => {
                  setParticipantName(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleNameSubmit();
                  }
                }}
                placeholder="Your name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}
              <button
                onClick={handleNameSubmit}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Join Session
              </button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Show error if session not found
  if (error && error.includes('not found')) {
    return (
      <div className="max-w-md mx-auto">
        <Card>
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go Home
            </button>
          </div>
        </Card>
      </div>
    );
  }

  if (!sessionId) {
    return null;
  }

  // Calculate vote states for button enabling/disabling
  // Check both participant votes and revealed votes (in case votes are revealed but participant state isn't synced)
  const hasAnyVotes = participants.some((p) => p.voted) || votesRevealed || revealedVotes.length > 0;
  const allVotesIn = participants.length > 0 && participants.every((p) => p.voted);

  return (
    <div className="space-y-4">
      <CelebrationOverlay
        isActive={showCelebration}
        duration={3000}
        onComplete={() => setShowCelebration(false)}
      />
      <SessionHeader
        sessionId={sessionId}
        elapsed={elapsed}
        onCopyLink={handleCopyLink}
        onLeaveSession={handleLeaveSession}
        state={state}
        hasAnyVotes={hasAnyVotes}
        allVotesIn={allVotesIn}
        onShowVotes={handleShowVotes}
        onReopenVoting={handleReopenVoting}
        onResetVotes={handleResetVotes}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <CollapsibleStory story={story} isFacilitator={isFacilitator}>
        {isFacilitator && (
          <StoryInput
            story={story}
            onChange={handleStoryUpdate}
            disabled={!connected}
          />
        )}
      </CollapsibleStory>

      <div className="grid grid-cols-1 lg:grid-cols-[52%_48%] gap-4">
        <div>
          <Card>
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              Cast Your Vote
            </h3>
            <PointsGrid
              onVote={handleVote}
              selectedValue={myVote || undefined}
              disabled={state === 'revealed' || !connected}
            />
          </Card>
        </div>

        <div>
          <Card>
            {participants.length === 0 ? (
              <EmptyState />
            ) : (
              <PlayerPanel 
                participants={participants} 
                state={state}
                voteSummary={votesRevealed ? voteSummary : null}
                isFacilitator={isFacilitator}
                onReset={handleResetVotes}
              />
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

